import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Play, Timer, X } from 'lucide-react';
import { timerPresetSubtitle, timerPresetTitle } from '@proerp/shared';
import { Button } from '@proerp/ui';
import { describeError } from '@/lib/apiClient';
import { useToasts } from '@/lib/toast';
import { expiresAt, useResumeSuggestion } from '@/features/time-tracking/resumeSuggestion';
import { useRunningTimer, useStartTimer } from '@/features/time-tracking/timeTrackingApi';

/**
 * Andockpunkt unten rechts. Mobil über der mobilen Leiste (`MobileDock`, oberer
 * Rand bei 4 rem über dem Safe-Area-Streifen → `bottom-16`),
 * ab `md` am unteren Rand. Die Breite wird gedeckelt, damit lange Projektnamen
 * die Karte auf schmalen Geräten nicht aus dem Bild schieben.
 */
const DOCK_CLASS = 'fixed bottom-16 right-4 z-50 max-w-[calc(100vw-2rem)] md:bottom-4';

/**
 * „Fortsetzen"-Vorschlag nach dem Stoppen (VB-26027) — auf jeder Seite sichtbar
 * (in der AppShell montiert, außerhalb des Outlets, damit er Routenwechsel
 * überlebt).
 *
 * Wer eine Aufzeichnung unterbricht, steht selten auf der Zeiterfassungsseite —
 * er ist irgendwo in der App. Ein Klick startet dieselbe Kombination erneut,
 * beschriftet mit denselben Funktionen wie die Kacheln „Zuletzt verwendet"
 * (`timerPresetTitle`/`timerPresetSubtitle`), damit überall dasselbe dasteht.
 *
 * Der LAUFENDE Timer sitzt seit 2026-09-16 in der Kopfzeile (`TimerPill`) — hier
 * steht nur noch der Vorschlag. Er blieb unten: Titel, Untertitel, „Fortsetzen"
 * und „Ausblenden" passen in keine 48-px-Leiste, und er ist eine kurzlebige
 * Einladung, kein Dauer-Element der Navigation.
 */
export function ResumeTimerCard() {
  const { data } = useRunningTimer();
  const startTimer = useStartTimer();
  const push = useToasts((state) => state.push);
  const location = useLocation();
  const resume = useResumeSuggestion((state) => state.suggestion);
  const dismissResume = useResumeSuggestion((state) => state.dismiss);
  const running = data?.data ?? null;

  // Der Vorschlag verfällt von selbst (§ RESUME_TTL_MS) — auch ohne Zutun des
  // Nutzers und ohne dass er dafür die Seite wechseln müsste.
  useEffect(() => {
    if (!resume) return;
    const remainingMs = expiresAt(resume) - Date.now();
    if (remainingMs <= 0) {
      dismissResume();
      return;
    }
    const id = window.setTimeout(dismissResume, remainingMs);
    return () => window.clearTimeout(id);
  }, [resume, dismissResume]);

  // Wurde nach dem Stoppen wieder ein Timer gestartet — hier, auf der
  // Zeiterfassungsseite oder am Handy —, ist der Vorschlag überholt.
  //
  // Verglichen wird bewusst mit dem Startzeitpunkt und nicht bloß „läuft ein
  // Timer": Direkt nach dem Stoppen kann die Abfrage kurz noch den eben
  // gestoppten Eintrag liefern (Refetch unterwegs). Der startete VOR dem Stoppen
  // und darf den frischen Vorschlag nicht löschen.
  useEffect(() => {
    if (!resume || !running?.startTime) return;
    if (new Date(running.startTime).getTime() >= new Date(resume.stoppedAt).getTime()) dismissResume();
  }, [running, resume, dismissResume]);

  const resumeTimer = useCallback(async () => {
    if (!resume) return;
    const { preset } = resume;
    try {
      await startTimer.mutateAsync({
        legalEntityId: preset.legalEntityId,
        categoryId: preset.categoryId,
        projectKind: preset.projectKind,
        projectId: preset.projectId,
        ttSubCategoryId: preset.ttSubCategoryId,
        note: preset.note,
      });
      dismissResume();
      push('success', 'Timer fortgesetzt.');
    } catch (error) {
      // Häufigster Fall: 409 „Es läuft bereits ein Timer." (anderes Gerät, zweiter
      // Tab). Die Servermeldung zeigen, statt still nichts zu tun — und den
      // Vorschlag stehen lassen, damit der Nutzer es nach dem Stoppen erneut kann.
      push('danger', describeError(error));
    }
  }, [resume, startTimer, dismissResume, push]);

  // Auf dem Zeiterfassungs-Dashboard zeigen die Kacheln „Zuletzt verwendet"
  // dasselbe Angebot — kein Doppel.
  if (location.pathname === '/zeiterfassung') return null;
  if (!resume) return null;

  const title = timerPresetTitle(resume.preset);
  const resumeLabel = `Fortsetzen: ${title}`;

  return (
    <div className={DOCK_CLASS}>
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-lg sm:flex-row sm:items-center sm:gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <Timer className="size-4" strokeWidth={1.75} aria-hidden />
          </span>
          <div className="min-w-0 max-w-[18rem]">
            <p className="truncate text-sm font-medium text-foreground">{title}</p>
            <p className="truncate text-xs text-muted-foreground">{timerPresetSubtitle(resume.preset)}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 max-sm:justify-end">
          <Button
            variant="primary"
            className="shrink-0 touch:h-11"
            isLoading={startTimer.isPending}
            onClick={resumeTimer}
            aria-label={resumeLabel}
          >
            <Play className="size-4" strokeWidth={1.5} aria-hidden />
            Fortsetzen
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 touch:size-11"
            onClick={dismissResume}
            aria-label="Vorschlag ausblenden"
          >
            <X className="size-4" strokeWidth={1.5} aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
